<template>
    <div :class="{'!my-0 mx-24 py-24': $route.name === 'multi-posting'}">
        <div v-if="$route.name === 'campaigns'">
            <feature
                feature-key="campaigns"
                sub-key="page"
                message-if-no-access
            >
                <div class="w-full xl:w-5/6 max-w-1440 mx-auto" v-if="can.view">
                    <header class="flex justify-between">
                        <action-button
                            @click="activeState = activeState === 'active' ? 'archived' : 'active'"
                            no-icon
                        >
                            <template #before>
                                <checkbox
                                    :checked="activeState === 'archived'"
                                    @change="activeState = activeState === 'active' ? 'archived' : 'active'"
                                />
                            </template>
                            <div class="capitalize">{{ $t('campaigns.archive') }}</div>
                        </action-button>

                        <simple-button
                            display="secondary"
                            :disabled="hasMultiPostingSubscription"
                            look="primary"
                            icon="add-circle"
                            v-if="can.manage"
                            @click="$router.push({ name: 'campaigns.create' })"
                        >
                            {{ $t('campaigns.add_new') }}
                        </simple-button>
                    </header>

                    <loading v-if="fetchingCampaigns" class="mt-64"/>
                    <div class="gap-16" v-else-if="campaigns.length > 0">
                        <div v-for="campaign in campaigns"
                             class="bg-white shadow-sm p-24 mt-16 flex rounded-6 border border-white trans-ease transition-all duration-150 relative justify-between items-center"
                             :class="can.manage ? 'cursor-pointer hover:shadow hover:border-primary-20' : 'cursor-not-allowed'">
                            <div class="grow" @click="goToCampaignDetail(campaign)">
                        <span class="text-body font-semibold">
                            <LabelWithMissingTranslation :label="campaign.job_title" :override-locale="campaign.locale"/>
                        </span>

                                <div class="flex justify-between mt-8">
                                    <div class="flex items-center space-x-16 text-primary-80">
                                        <span class="inline-flex items-center" v-if="campaign.date_start">
                                            <svg-icon type="calendar-today" class="text-primary-40 max-w-16 max-h-16 mr-4"/>
                                            {{ campaignStartsAt(campaign) }}
                                        </span>
                                        <span class="inline-flex items-center">
                                            <currency/>
                                            <span>{{ campaign.price?.toFixed(2).replace('.', ',') }}</span>
                                        </span>
                                        <span v-if="campaign.date_start" class="inline-flex items-center">
                                            <svg-icon type="schedule" class="text-primary-40 max-w-16 max-h-16 mr-4"></svg-icon>
                                            <span>{{ campaign.duration }} {{ $t('campaigns.days') }}</span>
                                        </span>
                                        <span>#{{ campaign.id }}</span>
                                    </div>
                                </div>

                                <div class="mt-12">
                                    <campaign-status :loading="fetchingStatus" :campaign="campaign"/>
                                </div>
                            </div>

                            <div class="inline-flex">
                                <span class="font-semibold inline-block text-center mr-32">
                                    <span class="block mb-8">{{$t('campaigns.jobboard_count')}}</span>
                                    <span class="bg-primary text-white p-4 inline-flex justify-center items-center rounded-32 circle">
                                        {{ getJobboardChannelCount(campaign) }}
                                    </span>
                                </span>
                                <div class="font-semibold inline-block text-center mr-32">
                                    <span class="block mb-8">{{$t('campaigns.social_media_boost')}}</span>
                                    <social-media-icons :media="getSocialMediaIcons(campaign)" class="space-x-8"/>
                                </div>
                            </div>
                            <span class="inline-block border-l-2 border-r border-primary-20 px-16 text-center mr-32" :class="[campaign.application_count  ? 'text-primary' : 'text-primary-60' ]">
                                <span class="application-count block mb-4 relative">
                                    <span class="text-big" :class="campaignHasTracking(campaign) ? 'font-semibold' : ''">
                                        {{ campaignHasTracking(campaign) ? campaign.application_count : '-' }}
                                    </span>
                                    <span class="absolute top-0 right-0" v-if="!campaignHasTracking(campaign)">
                                        <simple-dropdown
                                            :container="false"
                                            position="center"
                                        >
                                            <template #trigger>
                                                <svg-icon class="text-primary-60" type="help"/>
                                            </template>
                                            <template #content>
                                                <div class="bg-white p-4 w-320">
                                                    {{ $t('campaigns.no_tracking') }}
                                                </div>
                                            </template>
                                        </simple-dropdown>
                                    </span>
                                </span>
                                <span>{{$t('jobs.candidates')}}</span>
                            </span>
                            <div class="flex items-center pointer-events-none">
                                <simple-dropdown
                                    position="right"
                                    class="pointer-events-auto "
                                >
                                    <template #trigger>
                                        <round-button icon="more-vert" :disabled="!can.manage || !campaign.draft"/>
                                    </template>

                                    <template #content="{ close }">
                                        <nav
                                            aria-orientation="vertical"
                                            class="bg-white rlative rounded shadow"
                                            role="menu"
                                        >
                                            <dropdown-item
                                                danger
                                                :input-label="$t('campaigns.delete_draft')"
                                                @click="startDeletingCampaign(campaign)"
                                            />
                                        </nav>
                                    </template>
                                </simple-dropdown>
                            </div>
                        </div>
                    </div>
                    <empty-state
                        v-else
                        :button-text="$t('campaigns.add_new')"
                        button-color="secondary"
                        :description="activeState === 'archived' ? $t('campaigns.no_past_campaigns') : $t('campaigns.no_campaigns')"
                        :disabled="hasMultiPostingSubscription"
                        :show-animation="false"
                        button-icon="add-circle"
                        class="mt-48"
                        @click="$router.push({ name: 'campaigns.create' })"
                    >
                    </empty-state>

                    <popup-modal-confirm
                        v-if="!!republishingCampaign"
                        confirm-button="success"
                        :title="$t('campaigns.duplicate_confirmation').toString()"
                        :working="performingRepublishRequest"
                        @close="republishingCampaign = false"
                        @confirm="republishCampaign"
                    />
                    <popup-modal-confirm
                        show-exclamation
                        :title="$t('campaigns.delete_draft_confirmation').toString()"
                        v-if="!!deletingCampaign"
                        :working="performingDeleteRequest"
                        @close="stopDeletingCampaign"
                        @confirm="deleteCampaign"
                    />
                </div>
            </feature>
        </div>
        <div v-if="$route.name === 'multi-posting'">
            <feature feature-key="multiposting">
                <CampaignList ref="campaignList" :has-multi-posting-subscription="hasMultiPostingSubscription" />
            </feature>
        </div>

    </div>
</template>

<script>
import SimpleButton from "../components/ui/SimpleButton";
import SvgIcon from '../components/ui/SvgIcon';
import * as campaigns from '../services/CampaignService';
import Campaign from "../models/Campaign";
import {normalizeJson} from "../../shared/helpers";
import Loading from "../components/ui/Loading";
import RoundButton from "../components/ui/RoundButton";
import CampaignStatus from "../components/CampaignStatus";
import SimpleDropdown from "../components/ui/SimpleDropdown";
import PopupModalConfirm from "../components/ui/PopupModalConfirm";
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import SelectDropdown from "../components/forms/SelectDropdown";
import Feature from "../components/Feature";
import EarlyAccessBadge from "../components/EarlyAccessBadge";
import EmptyState from '../components/ui/EmptyState';
import LabelWithMissingTranslation from "../components/LabelWithMissingTranslation.vue";
import moment from "moment/moment";
import * as boosterService from "../services/BoosterService";
import Booster from "../models/Booster";
import SocialMediaIcons from "../components/SocialMediaIcons.vue";
import Checkbox from "../components/forms/Checkbox.vue";
import Currency from '../components/Currency';
import PaidSubscription from "../models/PaidSubscription";
import CampaignList from "../components/multiposting/CampaignList.vue";
import DropdownItem from "../components/ui/DropdownItem.vue";
import ActionButton from "../components/ui/ActionButton.vue";

export default {
    name: 'Campaigns',
    components: {
        ActionButton,
        DropdownItem,
        CampaignList,
        Checkbox,
        LabelWithMissingTranslation,
        EmptyState,
        EarlyAccessBadge,
        Feature,
        SelectDropdown,
        PopupModalConfirm,
        SimpleDropdown, CampaignStatus, RoundButton, Loading, SimpleButton, SvgIcon,
        SocialMediaIcons,
        Currency,
    },
    data() {
        return {
            noIntegration: true,
            fetchingCampaigns: false,
            activeState: 'active',
            fetchingStatus: false,
            republishingCampaign: false,
            deletingCampaign: false,
            performingRepublishRequest: false,
            performingDeleteRequest: false,
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        hasMultiPostingSubscription() {
            return !!this.multiPostingSubscription && this.currentCompany.multiposting_status === 'active';
        },
        multiPostingSubscription() {
            return useRepo(PaidSubscription)
                .where('type', 'multi-posting')
                .where('company_id', this.currentCompany.id)
                .where('active', true)
                .first();
        },
        can() {
            return {
                view: this.$can('campaigns.view'),
                manage: this.$can('campaigns.manage')
            };
        },
        campaigns() {
            return useRepo(Campaign)
                .with('job')
                .where('archived', this.activeState === 'archived')
                .where((campaign) => {
                    return campaign.limited === false || campaign.draft === !campaign.draft;
                })
                .orderBy('date_start', 'desc')
                .get();
        }
    },
    methods: {
        ...mapActions(useMainStore, ['setDocumentTitle']),
        fetch(archived = false) {
            this.fetchingCampaigns = true;
            this.campaignIds = [];

            campaigns.index(archived).then(result => {
                this.campaignIds = result.data.map( campaign => campaign.id );
                return useRepo(Campaign).save(normalizeJson(result.data))
            }).then(() => {
                this.fetchingStatus = true;
                campaigns.brockmeyerStatus(this.campaignIds).then(result => {
                    this.fetchingStatus = false;
                    if(result) {
                        return useRepo(Campaign).save(normalizeJson(result.data))
                    }
                });

                this.fetchingCampaigns = false;
            });
        },
        startAddingCampaign() {
            this.addingCampaign = true;
        },
        stopAddingCampaign() {
            this.addingCampaign = false;
        },
        startEditing(id) {
            if (!this.can.manage)
                return;

            this.editingCampaign = id;
        },
        stopEditing() {
            this.editingCampaign = false;
        },
        goToCampaignDetail(campaign) {
            this.$router.push({name: 'campaign', params: {id: campaign.id}})
        },
        campaignStartsAt(campaign) {
            return campaign.date_start ? moment(campaign.date_start).locale(this.locale).format('LL') : null;
        },
        startRepublishCampaign(campaign) {
            this.republishingCampaign = campaign;
        },
        startDeletingCampaign(campaign) {
            this.deletingCampaign = campaign;
        },
        stopDeletingCampaign() {
            this.deletingCampaign = false;
        },
        republishCampaign() {
            this.performingRepublishRequest = true;

            campaigns.republish(this.republishingCampaign.id).then((result) => {
                this.$router.push({name: 'campaign', params: {id: result.campaign_id}});
            });
        },
        deleteCampaign() {
            this.performingDeleteRequest = true;

            campaigns.update(this.deletingCampaign.id, {
                data: {
                    attributes: {
                        status: 'cancel'
                    }
                }
            }).then(() => {
                return campaigns.destroy(this.deletingCampaign.id);
            }).then(() => {
                return useRepo(Campaign).destroy(this.deletingCampaign.id);
            }).then(() => {
                this.deletingCampaign = false;
                this.performingDeleteRequest = false;
            })
        },
        getJobboardChannelCount(campaign) {
            return campaign.channels.filter(channel => !channel.booster_id).length;
        },
        getSocialMediaIcons(campaign) {
            let icons = {
                "google": false, "instagram": false, "facebook": false
            };

            let channel = campaign.channels.find(channel => channel.booster_id);

            if (!channel) {
                return icons;
            }

            const booster = useRepo(Booster).where('id', channel.booster_id).first();

            if (booster) {
                icons = booster.media
            }

            return icons;
        },
        campaignHasTracking(campaign) {
            return moment(campaign.created_at).isAfter(moment(1674691200 * 1000)) ||
                campaign.application_count > 0;
        },
    },
    watch: {
        activeState(val) {
            useRepo(Campaign).flush();
            this.fetch(val === 'archived')
        }
    },
    mounted() {
        this.setDocumentTitle(this.$t('campaigns.title'));

        if (this.$route.name !== 'multi-posting') {
            boosterService.index().then(({data}) => {
                return useRepo(Booster).save(normalizeJson(data));
            }).then(() => {
                this.fetchingBoosters = false;
            })

            this.fetch();
        }
    }
}
</script>

<style scoped>
.circle {
    width: 32px;
    height: 32px;
}

button.edit {
    &:not(.is-active):hover {
        transform: scale(1.1);
    }
}

 .tab-buttons {
& input[type="radio"] {
      @apply hidden;

& + label {
    background: #DDE4E6;

    &:hover{
    background: #C5D3D9;
     }
      @apply inline-flex items-center h-40 rounded-6 text-primary-80 font-semibold px-16 cursor-pointer;
  }

&:checked + label {
     background: #38464B;
     color: #FFF;
 }
}
}
</style>
